import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import enCommon from './locales/en/common.json';
import frCommon from './locales/fr/common.json';
import esCommon from './locales/es/common.json';
import ruCommon from './locales/ru/common.json';
import idCommon from './locales/id/common.json';

/**
 * i18n is wired from the start — zero hard-coded UI text anywhere.
 * Back-office + owner portal (apps/web): en (default), fr, es, ru.
 * Technician PWA (apps/field):           en (default), id.
 * The resource set below is the union; each app initialises its own subset.
 */

export const DEFAULT_LOCALE = 'en' as const;
export const WEB_LOCALES = ['en', 'fr', 'es', 'ru', 'id'] as const; // id: Indonesian on web + portal (A.6 M9); untranslated keys fall back to en
export const FIELD_LOCALES = ['en', 'id'] as const;
export const ALL_LOCALES = ['en', 'fr', 'es', 'ru', 'id'] as const;

export type Locale = (typeof ALL_LOCALES)[number];

const RESOURCES: Record<Locale, { common: Record<string, unknown> }> = {
  en: { common: enCommon },
  fr: { common: frCommon },
  es: { common: esCommon },
  ru: { common: ruCommon },
  id: { common: idCommon },
};

export function isLocale(value: string | undefined | null): value is Locale {
  return !!value && (ALL_LOCALES as readonly string[]).includes(value);
}

/**
 * Creates an isolated i18next instance restricted to `supported` locales.
 * `requested` is coerced to a supported locale or the default.
 */
export function createI18n(
  supported: readonly Locale[],
  requested?: string | null,
): I18nInstance {
  const lng = isLocale(requested) && supported.includes(requested) ? requested : DEFAULT_LOCALE;
  const resources = Object.fromEntries(supported.map((l) => [l, RESOURCES[l]]));

  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...supported],
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}
